/**
 * Prompt Editor Page
 *
 * Main container for viewing and editing AI prompts.
 * Allows users to customize prompts and model settings for each prompt.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Container,
  Title,
  Text,
  Chip,
  Group,
  Badge,
  Loader,
  Alert,
  Button,
  SimpleGrid,
  TextInput,
  Select,
  Divider
} from '@mantine/core';
import {
  IconSparkles,
  IconAlertCircle,
  IconRefresh,
  IconSearch,
  IconFilter
} from '@tabler/icons-react';

import {
  fetchPrompts,
  selectAllPrompts,
  selectBuilderPrompts,
  selectExplorerPrompts,
  selectSecurityPrompts,
  selectPromptsLoading,
  selectPromptsError,
  selectPromptPermissions
} from '../../redux/slices/promptsSlice';

import PromptCard from './PromptCard';
import PromptEditorModal from './PromptEditorModal';

// Flow display names
const FLOW_LABELS = {
  riskIdentification: 'Risk Identification',
  riskEvaluation: 'Risk Evaluation',
  riskScenarios: 'Risk Scenarios',
  actions: 'Mitigating Actions',
  security: 'Security',
  residualRisk: 'Residual Risk',
  actionPlan: 'Action Plan',
  narrative: 'Narrative',
  riskAnalysis: 'Risk Analysis',
  research: 'Research',
  scenarios: 'Scenarios',
  prioritization: 'Prioritization'
};

const PromptEditorPage = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  // Redux state
  const allPrompts = useSelector(selectAllPrompts);
  const builderPrompts = useSelector(selectBuilderPrompts);
  const explorerPrompts = useSelector(selectExplorerPrompts);
  const securityPrompts = useSelector(selectSecurityPrompts);
  const loading = useSelector(selectPromptsLoading);
  const error = useSelector(selectPromptsError);
  const permissions = useSelector(selectPromptPermissions);

  // Local state
  const [activeTab, setActiveTab] = useState('builder');
  const [searchQuery, setSearchQuery] = useState('');
  const [flowFilter, setFlowFilter] = useState('all');
  const [editingPrompt, setEditingPrompt] = useState(null);

  // Store auth0 client globally for Redux thunks
  useEffect(() => {
    if (isAuthenticated) {
      window.auth0Client = { getAccessTokenSilently };
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Fetch prompts on mount
  useEffect(() => {
    dispatch(fetchPrompts());
  }, [dispatch]);

  // Filter prompts based on search and flow
  const filteredPrompts = useMemo(() => {
    let prompts = activeTab === 'builder' ? builderPrompts :
                  activeTab === 'explorer' ? explorerPrompts :
                  activeTab === 'security' ? securityPrompts :
                  allPrompts;

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      prompts = prompts.filter(prompt =>
        prompt.promptName?.toLowerCase().includes(query) ||
        prompt.description?.toLowerCase().includes(query) ||
        prompt.promptKey?.toLowerCase().includes(query)
      );
    }

    // Apply flow filter
    if (flowFilter !== 'all') {
      prompts = prompts.filter(prompt => prompt.promptFlow === flowFilter);
    }

    return prompts;
  }, [activeTab, builderPrompts, explorerPrompts, securityPrompts, allPrompts, searchQuery, flowFilter]);

  // Get unique flows for filter dropdown
  const availableFlows = useMemo(() => {
    const flows = new Set();
    const prompts = activeTab === 'builder' ? builderPrompts :
                    activeTab === 'explorer' ? explorerPrompts :
                    activeTab === 'security' ? securityPrompts :
                    allPrompts;
    prompts.forEach(p => flows.add(p.promptFlow));
    return Array.from(flows).sort();
  }, [activeTab, builderPrompts, explorerPrompts, securityPrompts, allPrompts]);

  // Group filtered prompts by flow for display
  const groupedPrompts = useMemo(() => {
    const grouped = {};
    filteredPrompts.forEach(prompt => {
      const flow = prompt.promptFlow || 'other';
      if (!grouped[flow]) {
        grouped[flow] = [];
      }
      grouped[flow].push(prompt);
    });
    return grouped;
  }, [filteredPrompts]);

  const handleRefresh = () => {
    dispatch(fetchPrompts());
  };

  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
  };

  const handleCloseEditor = () => {
    setEditingPrompt(null);
  };

  if (loading && allPrompts.length === 0) {
    return (
      <Container size="xl" py="xl">
        <Group justify="center" py="xl">
          <Loader size="lg" />
          <Text>Loading prompts...</Text>
        </Group>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      {/* Header */}
      <Group justify="space-between" align="center" mb="lg">
        <Group gap="sm">
          <IconSparkles size={24} stroke={1.5} color="var(--mantine-color-dimmed)" />
          <Title order={2} fw={500}>Prompt Editor</Title>
        </Group>
        <Button
          leftSection={<IconRefresh size={16} />}
          variant="light"
          onClick={handleRefresh}
          loading={loading}
        >
          Refresh
        </Button>
      </Group>

      {/* Error alert */}
      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title="Error loading prompts"
          mb="md"
          withCloseButton
        >
          {error}
        </Alert>
      )}

      {/* Permission notice */}
      {!permissions.canEdit && (
        <Alert
          color="blue"
          title="View Only"
          mb="md"
        >
          You can view prompt configurations but need the Risk Manager or Admin role to edit them.
        </Alert>
      )}

      {/* Flow chip filter */}
      <Group gap="xs" mb="sm">
        <Text size="sm" c="dimmed" fw={500} w={40}>Flow:</Text>
        <Chip.Group value={activeTab} onChange={setActiveTab}>
          <Group gap="xs">
            <Chip value="builder" variant="light" size="sm" radius="md" color="blue">
              Builder ({builderPrompts.length})
            </Chip>
            <Chip value="explorer" variant="light" size="sm" radius="md" color="grape">
              Explorer ({explorerPrompts.length})
            </Chip>
            <Chip value="security" variant="light" size="sm" radius="md" color="red">
              Security ({securityPrompts.length})
            </Chip>
            <Chip value="all" variant="light" size="sm" radius="md">
              All ({allPrompts.length})
            </Chip>
          </Group>
        </Chip.Group>
      </Group>

      <Divider mb="md" />

      {/* Search and filter */}
      <Group mb="lg">
        <TextInput
          placeholder="Search prompts..."
          leftSection={<IconSearch size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          style={{ flex: 1, maxWidth: 300 }}
        />
        <Select
          placeholder="Filter by flow"
          leftSection={<IconFilter size={16} />}
          value={flowFilter}
          onChange={setFlowFilter}
          data={[
            { value: 'all', label: 'All Flows' },
            ...availableFlows.map(flow => ({
              value: flow,
              label: FLOW_LABELS[flow] || flow
            }))
          ]}
          style={{ width: 200 }}
          clearable={false}
        />
      </Group>

      {/* Prompt cards grouped by flow */}
      {Object.keys(groupedPrompts).length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">
          No prompts found matching your filters.
        </Text>
      ) : (
        Object.entries(groupedPrompts).map(([flow, prompts]) => (
          <div key={flow} style={{ marginBottom: 32 }}>
            <Group mb="sm">
              <Text fw={600} size="lg" tt="uppercase" c="dimmed">
                {FLOW_LABELS[flow] || flow}
              </Text>
              <Badge size="sm" variant="light" color="gray">
                {prompts.length}
              </Badge>
            </Group>
            <SimpleGrid
              cols={{ base: 1, sm: 2, lg: 3 }}
              spacing="md"
            >
              {prompts.map(prompt => (
                <PromptCard
                  key={prompt.promptKey}
                  prompt={prompt}
                  onEdit={() => handleEditPrompt(prompt)}
                  canEdit={permissions.canEdit}
                />
              ))}
            </SimpleGrid>
          </div>
        ))
      )}

      {/* Editor modal */}
      <PromptEditorModal
        prompt={editingPrompt}
        opened={!!editingPrompt}
        onClose={handleCloseEditor}
      />
    </Container>
  );
};

export default PromptEditorPage;
